// source: template-project@7007a338a apps/backoffice/src/pages/onboarding-v2/QuestionCard.tsx (behaviour and markup)
// Ours on top of TP: aria-pressed buttons (no radio roles), a `committing` guard on the 180ms single-choice commit
// (TP double-fires and never clears the timer), keys 1-9 over the options still in the grid only (TP toggles
// hidden ranked ones), Ctrl in place of the command key off Apple platforms, the mount click guard, a live region
// that also announces removals, and pending rank text appended as the last rank.
import { ChevronLeft } from 'lucide-react';
import { useEffect, useId, useRef, useState } from 'react';

import { DockedQuestionCard } from '@/components/rooms';
import { IconButton } from '@/components/ui/icon-button';
import { Button } from '@/components/ui/shadcn';
import { cn } from '@/lib/utils';
import { isApplePlatform } from '@/lib/platform';

import { COPY } from '../copy';
import { PANEL_CLICK_GUARD_MS, useClickGuard } from '../hooks/useClickGuard';
import type { Answer, Question } from '../quiz/questions';
import { FreeTextRow } from './FreeTextRow';
import { OptionRow } from './OptionRow';
import { RankChipList, rankWithCustom } from './RankChips';

/** How long a single choice shows as selected before the answer is committed (TP). */
export const SINGLE_COMMIT_MS = 180;

export interface QuestionPanelProps {
  /** The question as shown for the answers so far (`visibleOptions` applied). */
  question: Question;
  /** The wording for the answers so far (`questionTitle`). */
  title: string;
  index: number;
  total: number;
  /** An earlier answer shown again (Back): its picks, typed text and ranking start filled in, and Next is offered. */
  initial?: Answer;
  onAnswer: (answer: Answer) => void;
  onSkip: () => void;
  /** Go to the previous question. Given from question 2 on, and only while the quiz is open. */
  onBack?: () => void;
  /** Clicks are ignored this long after the panel mounts (a double-click must not answer the next question too). */
  clickGuardMs?: number;
}

/**
 * One quiz question in the docked card, TP's QuestionCard:
 * - single choice shows the pick, then commits 180ms later (further clicks and keys are ignored meanwhile);
 * - multi and rank questions need Next (⌘↵ on Apple platforms, Ctrl↵ elsewhere), disabled until something is
 *   picked or typed; a revisited answer and typed text show Next too;
 * - Skip is always there (ghost) and sends no answer text; Back (from question 2) goes to the question before;
 * - keys 1-9 pick the Nth option still in the grid, Cmd/Ctrl+Enter is Next.
 * A free-text row (`own`, `else`) is an input in place; its text is the answer, or joins the ranking.
 * Text typed into a rank question's free-text row but not yet added still counts: Next includes it as the last rank.
 * Mount it with `key={question.id}` so its selection starts from `initial` for each question.
 */
export function QuestionPanel({
  question,
  title,
  index,
  total,
  initial,
  onAnswer,
  onSkip,
  onBack,
  clickGuardMs = PANEL_CLICK_GUARD_MS,
}: QuestionPanelProps) {
  const titleId = useId();
  const guard = useClickGuard(clickGuardMs);
  const [selected, setSelected] = useState<string[]>(() => (initial && !initial.skipped ? initial.selected : []));
  const [text, setText] = useState(() => initial?.text ?? '');
  const [announcement, setAnnouncement] = useState('');
  const [apple] = useState(isApplePlatform);
  /** The pending single-choice commit; `timer` is non-null while `committing`. */
  const commit = useRef<{ timer: number | null }>({ timer: null });

  const isRank = question.mode === 'rank';
  const isMulti = question.mode === 'multi' || isRank;
  const hasText = text.trim().length > 0;
  const canSubmit = selected.length > 0 || hasText;
  const revisiting = initial !== undefined && !initial.skipped;
  const showNext = isMulti || revisiting || hasText;
  const options = question.options.filter((o) => !o.freeText);
  const freeText = question.options.find((o) => o.freeText);
  const exclusiveIds = new Set(question.options.filter((o) => o.exclusive).map((o) => o.id));
  /** What the grid shows: in a ranking, an option leaves the grid once ranked and returns when its chip is removed. */
  const gridOptions = options.filter((o) => !isRank || !selected.includes(o.id));
  const labelOf = (id: string) => question.options.find((o) => o.id === id)?.label ?? id;
  // With a pinned #1 the ranked options count from 2.
  const firstRank = question.pinned ? 2 : 1;

  const committing = () => commit.current.timer !== null;
  const cancelCommit = () => {
    if (commit.current.timer !== null) window.clearTimeout(commit.current.timer);
    commit.current.timer = null;
  };
  // A pending commit must not outlive the panel: it would answer a question that is no longer on screen.
  useEffect(() => {
    const pending = commit.current;
    return () => {
      if (pending.timer !== null) window.clearTimeout(pending.timer);
      pending.timer = null;
    };
  }, []);

  const submit = guard(() => {
    if (!canSubmit || committing()) return;
    if (isRank) {
      const ranking = hasText ? (rankWithCustom(question, selected, text)?.ranked ?? selected) : selected;
      onAnswer({ selected: ranking });
      return;
    }
    onAnswer({ selected, ...(hasText ? { text: text.trim() } : {}) });
  });

  const skip = guard(() => {
    if (committing()) return;
    onSkip();
  });

  const back = guard(() => {
    cancelCommit();
    onBack?.();
  });

  const toggle = guard((id: string) => {
    if (committing()) return;
    const option = question.options.find((o) => o.id === id);
    if (!option) return;
    if (!isMulti) {
      // Single choice: show the pick, then commit. `committing` ignores everything else until then.
      setSelected([id]);
      setText('');
      commit.current.timer = window.setTimeout(() => {
        commit.current.timer = null;
        onAnswer({ selected: [id] });
      }, SINGLE_COMMIT_MS);
      return;
    }
    if (isRank && !selected.includes(id)) setAnnouncement(`${option.label} ranked ${selected.length + firstRank}`);
    setSelected((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      if (option.exclusive) return [id];
      return [...prev.filter((x) => !exclusiveIds.has(x)), id];
    });
    if (option.exclusive) setText('');
  });

  const removeRanked = guard((id: string) => {
    setSelected((prev) => prev.filter((x) => x !== id));
    setAnnouncement(`${labelOf(id)} removed from the ranking`);
  });

  /** Enter in a rank question's text row: the typed entry joins the ranking (a listed label ranks that option once). */
  const addTyped = guard(() => {
    const next = rankWithCustom(question, selected, text);
    if (next) {
      setSelected(next.ranked);
      setAnnouncement(`${labelOf(next.id)} ranked ${next.ranked.length - 1 + firstRank}`);
    }
    if (text.trim()) setText('');
  });

  const changeText = (next: string) => {
    // Typing your own answer replaces a single choice (and cancels its pending commit) or an exclusive one.
    cancelCommit();
    setText(next);
    if (!isMulti) setSelected([]);
    else setSelected((prev) => prev.filter((x) => !exclusiveIds.has(x)));
  };

  // Keys: 1-9 pick the Nth option still in the grid, Cmd/Ctrl+Enter is Next. One window listener for the panel's
  // life, calling the handler of the latest render (it closes over the selection).
  const onKey = (e: KeyboardEvent) => {
    if (e.repeat) return;
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      if (!canSubmit) return;
      e.preventDefault();
      submit();
      return;
    }
    if (e.metaKey || e.ctrlKey || e.altKey || !/^[1-9]$/.test(e.key)) return;
    const target = e.target as HTMLElement | null;
    if (target && (/^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName) || target.isContentEditable)) return;
    const option = gridOptions[Number(e.key) - 1];
    if (!option) return;
    e.preventDefault();
    toggle(option.id);
  };
  const onKeyRef = useRef(onKey);
  useEffect(() => {
    onKeyRef.current = onKey;
  });
  useEffect(() => {
    const listener = (e: KeyboardEvent) => onKeyRef.current(e);
    window.addEventListener('keydown', listener);
    return () => window.removeEventListener('keydown', listener);
  }, []);

  const hinted = options.some((o) => o.hint);

  return (
    <DockedQuestionCard
      focusHeading
      title={title}
      subtitle={question.subtitle}
      titleId={titleId}
      index={index}
      total={total}
      footer={
        <>
          {onBack ? <IconButton label={COPY.back} icon={ChevronLeft} tone="ghost" size="md" onClick={back} /> : null}
          <span className="flex-1" />
          <Button type="button" variant="ghost" size="xs" onClick={skip}>
            {COPY.skip}
          </Button>
          {showNext ? (
            <Button type="button" size="xs" disabled={!canSubmit} onClick={submit}>
              {COPY.next}
              <kbd className="text-meta opacity-70" aria-label={apple ? 'Command Enter' : 'Control Enter'}>
                {apple ? '⌘↵' : 'Ctrl↵'}
              </kbd>
            </Button>
          ) : null}
        </>
      }
    >
      {isRank && (question.pinned || selected.length > 0) ? (
        <RankChipList pinned={question.pinned} ranked={selected} labelOf={labelOf} onRemove={removeRanked} />
      ) : null}

      <div
        role="group"
        aria-labelledby={titleId}
        className={cn(
          'grid gap-1.5',
          // Options with a line under them need the room: two columns, not three.
          hinted ? '[grid-template-columns:repeat(auto-fill,minmax(280px,1fr))]' : '[grid-template-columns:repeat(auto-fill,minmax(200px,1fr))]',
        )}
      >
        {gridOptions.map((o) => (
          <OptionRow
            key={o.id}
            label={o.label}
            hint={o.hint}
            icon={o.icon}
            selected={selected.includes(o.id)}
            onClick={() => toggle(o.id)}
          />
        ))}
      </div>

      {freeText ? (
        <FreeTextRow
          label={freeText.label}
          icon={freeText.icon}
          value={text}
          onChange={changeText}
          onSubmit={isRank ? addTyped : submit}
        />
      ) : null}

      <span className="sr-only" aria-live="polite">
        {announcement}
      </span>
    </DockedQuestionCard>
  );
}
